/**
 * List tests in a gate and list filters in a mapping (Feature 77): a list
 * test is told from a rule by its operator, and a list filter from a plain
 * reference by carrying conditions or a picked field (D21, D25).
 */
import type {
  LinkageCountOperator,
  LinkageListFilter,
  LinkageListOperator,
  LinkageListTest,
  LinkageRule,
  LinkageSource,
} from '@optum/api-client/types';

/** What an inner rule or a pick reads: one item of the list (D21, D25). */
export const ITEM = 'item';

export const LIST_OPERATORS: readonly LinkageListOperator[] = [
  'any_where',
  'every_where',
  'count_where',
];

export const LIST_OPERATOR_LABELS: Record<LinkageListOperator, string> = {
  any_where: 'any item where',
  every_where: 'every item where',
  count_where: 'count of items where',
};

/** How a count of matching items may be compared with its whole number (D21). */
export const COUNT_OPERATORS: readonly LinkageCountOperator[] = [
  'equals',
  'not_equals',
  'greater_than',
  'greater_or_equal',
  'less_than',
  'less_or_equal',
];

/** Whether an operator is one of the three list tests. */
export function isListOperator(op: string): op is LinkageListOperator {
  return (LIST_OPERATORS as readonly string[]).includes(op);
}

/** A new item condition: the item itself, present. */
export function itemCondition(): LinkageRule {
  return { ref: ITEM, op: 'exists' };
}

/** A list test on `ref` with one item condition; a count starts at "more than 0". */
export function toListTest(ref: string, op: LinkageListOperator): LinkageListTest {
  const test: LinkageListTest = { ref, op, where: { join: 'all', items: [itemCondition()] } };
  return op === 'count_where' ? { ...test, count: { op: 'greater_than', value: 0 } } : test;
}

/** The test with another list operator: a count kept, started, or dropped as it needs. */
export function withListOperator(test: LinkageListTest, op: LinkageListOperator): LinkageListTest {
  const { count, ...rest } = test;
  if (op !== 'count_where') return { ...rest, op };
  return { ...rest, op, count: count ?? { op: 'greater_than', value: 0 } };
}

/** Whether a gate's item is a list test rather than a rule on one value. */
export function isListTest(rule: LinkageRule | LinkageListTest): rule is LinkageListTest {
  return isListOperator(rule.op);
}

/** Whether a mapping source is a list filter rather than a plain reference or a fixed value. */
export function isListFilter(source: LinkageSource): source is LinkageListFilter {
  return 'ref' in source && ('where' in source || 'pick' in source);
}

// Types that travel the same on the wire, as the agent's save check groups
// them (Feature 77 D26): a UUID, an enum, and a date go as text; an integer
// and a number as a number.
const FAMILIES: Readonly<Record<string, string>> = {
  string: 'text',
  enum: 'text',
  datetime: 'text',
  integer: 'number',
  number: 'number',
};

/** Whether a list of `given` may fill a list of `wanted`; `any` agrees with every type. */
export function typesAgree(wanted: string, given: string): boolean {
  if (wanted === 'any' || given === 'any') return true;
  return (FAMILIES[wanted] ?? wanted) === (FAMILIES[given] ?? given);
}
